import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import { Affix, Alert, Button, Card, DatePicker, Form, Input, InputNumber, Layout, 
    Radio, message, Space, Spin } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Routes } from '../../routes';
import { LanguageSelectorSimple } from '../../components/formItems';
import { useCreateEditLocales } from '../../hooks';
import { DEFAULT_LOCALE_CODE } from '../../const'; 
import RichTextEditor from '../../components/controls/RichTextEditor/RichTextEditor';
import { buildPageHeaderBreadcrumb } from '../../updated/utils';
import { Loadable } from '../../components/loadable/Loadable';
import { useCreateNotificationMutation, usePatchNotificationMutation,
    useNotificationQuery, useLocalesQuery, useDeleteNotificationMutation } from '../../gql/hooks';
import DeleteButton from '../../components/DeleteButton';
import TinyMCE from '../../components/TinyMCE/TinyMCE';
import { CompanyContext } from '../../providers/companyProvider';    
import { useSegmentation } from '../../hooks/useSegmentation';
import { useFeatureFlag } from '../../featureFlags';
import { flags } from '../../featureFlags/flags';
import './styles.less'; 

dayjs.extend( isSameOrBefore );

const CreateEditNotificationView = ( props ) => {
    const { company } = useContext( CompanyContext );
    const TinyMCERef = useRef( null );
    const showTinyMCE = useFeatureFlag( { name: flags.USE_TINY_MCE } );
    const [ isEditing ] = useState( ( props?.route?.params?.notificationId ) ); 
    const [ isSaving, setIsSaving ] = useState( false ); 
    const [ timeZone ] = useState( Intl.DateTimeFormat().resolvedOptions().timeZone );
    const [ notificationId ] = useState( props?.route?.params?.notificationId );
    const [ showFeaturedHelp, setShowFeaturedHelp ] = useState( false );
    const [ showStickyHelp, setShowStickyHelp ] = useState( false );
    const [ showPublishedHelp, setShowPublishedHelp ] = useState( false );
    const [ companyDefaultLocaleCode ] = useState( company?.defaultLocaleCode || DEFAULT_LOCALE_CODE );
    const [ createNotification ] = useCreateNotificationMutation();
    const [ patchNotification ] = usePatchNotificationMutation();
    const [ deleteNotification ] = useDeleteNotificationMutation();
    const [ formController ] = Form.useForm();
    const [ invalidLocaleError, setInvalidLocaleError ] = useState( false );
    // eslint-disable-next-line no-unused-vars
    const [ currentLocaleCode, setLocales, descriptionRawHTML, handleChangeLocale, setCurrentLocaleCode, 
        validateEnglishExists ] = useCreateEditLocales( formController );

    const initialFormValues = {
        id: notificationId,
        isSticky: false,
        isPublished: false, 
        isFeatured: false,
        weight: 5,
        brandIds: [],
        segmentIds: [],
        localeIds: [], 
        productIds: [],
        learningProfileIds: [],
        companyRelationshipIds: [],
        employeeLocaleIds: [],
        _publishAt: undefined,
        _unpublishAt: undefined,
        _unfeatureAt: undefined,
        _locale: DEFAULT_LOCALE_CODE 
    };

    const { loading: segmentationLoading, initializeSegmentation, saveSegmentation, 
        renderSegmentation } = useSegmentation( formController );
    
    /*************************************
     *  Load notification query handling
     *************************************/
 
    const { fetch: fetchNotification, data: notificationData, loading: notificationLoading, 
        error: notificationError, called: notificationCalled } = 
        useNotificationQuery( notificationId, { fetchPolicy: 'network-only' } );

    const notification = useMemo( () => {
        if ( isEditing && notificationId &&  notificationData?.notification?.node ) {
            const node = notificationData.notification.node;

            // Removed locales with null values
            let filtered = node?.locales ? node.locales.filter( 
                l => l.name !== null || l.description !== null )
                : [];

            setLocales( filtered.map( l => ( { 
                locale: l.code, 
                name: l.name, 
                description: l.description 
            } ) ) );
 
            return {
                id: notificationId,
                isSticky: node.isSticky,
                isPublished: node.isPublished, 
                isFeatured: node.isFeatured,
                weight: node.weight,
                brandIds: node.brandIds || [],
                segmentIds: node.segmentIds || [],
                localeIds: node.localeIds || [],
                productIds: node.productIds || [],
                learningProfileIds: node.learningProfileIds || [],
                companyRelationshipIds: node.companyRelationshipIds || [],
                employeeLocaleIds: node.employeeLocaleIds || [],
                _publishAt: node.publishAt ? dayjs( node.publishAt ) : undefined,
                _unpublishAt: node.unpublishAt ? dayjs( node.unpublishAt ) : undefined,
                _unfeatureAt: node.unfeatureAt ? dayjs( node.unfeatureAt ) : undefined,
                _locale: DEFAULT_LOCALE_CODE
            };
        } else {
            return undefined;
        }
    }, [ isEditing, notificationId, notificationData ] );
    
    // Trigger loading the notification
    useEffect( () => {
        if ( isEditing && notificationId ) {
            fetchNotification();
        }
    }, [ isEditing, notificationId ] );
    
    useEffect( () => {
        if ( isEditing && notification && !segmentationLoading ) {
            const segmentation = {
                node: {
                    brandIds: notification.brandIds,
                    segmentIds: notification.segmentIds,
                    localeIds: notification.localeIds, 
                    productIds: notification.productIds,
                    learningProfileIds: notification.learningProfileIds,
                    companyRelationshipIds: notification.companyRelationshipIds,
                    employeeLocaleIds: notification.employeeLocaleIds,
                }
            };

            initializeSegmentation( segmentation );
        }
    }, [ isEditing, notification, segmentationLoading ] );

    /*************************************
     *  Locales query handling
     *************************************/
 
    const { data: localesData, loading: localesLoading, error: localesError } = 
        useLocalesQuery( { fetchPolicy: 'cache-first' } );

    const defaultLocaleName = useMemo( () => {
        const companyLocaleCode = companyDefaultLocaleCode;
        const localeList = localesData?.locales?.edges || [];
        const locale = localeList.find( i => i.node.code === companyLocaleCode );
        
        if ( locale ) {
            return locale.node.labelEnUs;
        } else {
            return companyLocaleCode;
        }
    }, [ localesData, company ] );
    
    const locales = useMemo( () => {
        if ( !localesData ) { 
            return undefined; 
        }
       
        const mappedData = localesData.locales.edges.map( i => ( { 
            key: i.node.code,
            label: i.node.labelEnUs,
        } ) );

        return mappedData.sort( ( optionA, optionB ) => {
            const labelOptionA = ( optionA?.label?.toLowerCase() || '' );
            const labelOptionB = ( optionB?.label?.toLowerCase() || '' );
            return labelOptionA.localeCompare( labelOptionB );
        } );
    }, [ localesData ] );
    
    /*************************************
     *  Loading and error state handling
     *************************************/

    const isLoading = useMemo( () => ( isSaving ? false : segmentationLoading ) || localesLoading || notificationLoading || 
        ( isEditing ? !notificationCalled : false ),
        [ notification, localesLoading, isEditing, notificationCalled, notificationLoading, 
            isSaving, segmentationLoading ] );
    const hasError = useMemo( () => notificationError || localesError, 
        [ notificationError, localesError ] );

    /*************************************
     *  Form submission handling
     *************************************/
    
    const handleFormSubmission = ( values ) => {
        
        let formLocales = handleChangeLocale( currentLocaleCode ).map( i => ( {
            code: i.locale,
            name: i.name,
            description: i.description
        } ) ).filter( i => !( i.name === '' && i.description  === '' ) );
        
        const invalidLocales = formLocales.filter( l => 
            l.name === '' || l.description === '' || l.description === '<p><br></p>' );

        if ( invalidLocales && invalidLocales.length > 0 ) {
            const invalidMessage = invalidLocales.map( l => {
                
                let localeName = locales.find( n => n.key === l.code );
               
                if ( l.name === '' ) {
                    return <span key={ l.locale }>{ `${ localeName?.label } is required to have a title` }<br/></span>;
                }
                if ( l.description === '' ) {
                    return <span key={ l.locale }>{ `${ localeName?.label } is required to have content` }<br/></span>;
                }
            } );

            setInvalidLocaleError( invalidMessage );
            
            return;
        } else {
            setInvalidLocaleError( false );
        }

        setIsSaving( true );

        // Setup unfeatureAt date
        let unfeatureAt = null;
        if ( values.isFeatured && values._unfeatureAt ) {
            unfeatureAt = dayjs.utc( values._unfeatureAt ).format();
        }
       
        // Setup publishAt date
        let publishAt = null;
        if ( !values.isPublished && values._publishAt ) {
            publishAt = dayjs.utc( values._publishAt ).format();
        }
     
        // Setup unpublishAt date
        let unpublishAt = null;
        if (  values._unpublishAt ) { 
            unpublishAt = dayjs.utc( values._unpublishAt ).format();
        }
      
        // Create the input for the mutation
        const input = { 
            id: isEditing ? notification.id : undefined,
            isPublished: values.isPublished,
            isFeatured: values.isFeatured,
            isSticky: values.isSticky,
            weight: values.weight || 5,
            publishAt,
            unpublishAt,
            unfeatureAt,
            locales: formLocales
        };

        saveNotification( input, values, values.brandIds, values.segmentIds );
    };

    const saveNotification = async ( input, formValues, brandIds, segmentIds ) => {
        try {
            const response = isEditing ?
                await patchNotification( { variables: { input } } ) :
                await createNotification( { variables: { input } } );
            
            const success = !( response?.error || response?.errors && response?.errors?.length > 0 );

            if ( success ) {
                await Promise.all( [ saveSegmentation( formValues, response.data.result.id, 'notification' ) ] );
                
                setTimeout( () => {
                    message.success( 'Notification saved' );
                    window.history.back();
                    setIsSaving( false );
                }, 7000 );
            } else {
                message.error( 'Error saving notification!' );
                setIsSaving( false );
            }
        } catch ( e ) { 
            console.error( e ); 
            message.error( 'Error saving notification!' );
            setIsSaving( false );
        }

    };

    /*************************************
     *  Event handling
     *************************************/
    
    const handleFeaturedHelpClick = () => {
        setShowFeaturedHelp( !showFeaturedHelp );
    };
    
    const handleStickyHelpClick = () => {
        setShowStickyHelp( !showStickyHelp );
    };

    const handlePublishedHelpClick = () => {
        setShowPublishedHelp( !showPublishedHelp );
    };
 
    const handleDeleteClick = async ( ) => {
        setIsSaving( true );

        try {
            await deleteNotification( { variables: { input: { id: notificationId } } } );
            message.success( 'Notification deleted' );
            window.history.back();
        } catch ( e ) { 
            console.error( e ); 
            message.error( 'Error deleting notification!' );
            setIsSaving( false );
        }
    };

    /*************************************
     *  Returned JSX
     *************************************/
    
    return (
        <Layout className="create-edit-notification-view">
            <Layout>
                { /* Breadcrumbs */ }
                <PageHeader
                    breadcrumb={ buildPageHeaderBreadcrumb( [
                        { breadcrumbName: 'Course Studio', path: Routes.Home.buildPath() },
                        { breadcrumbName: 'Notifications', path: Routes.NotificationsPage.buildPath() },
                        { breadcrumbName: isEditing ? 'Edit' : 'Create' }
                    ] ) } 
                />
 
                { /* Loading Spinner */ }
                { isLoading &&
                <Spin size="medium"/>
                }

                { /* Main Content */ }
                { !isLoading && 
                <Card
                    className="notification-content"
                    title={ `${ isEditing ? 'Edit' : 'Create' } Notification` }
                >   
                    { hasError && 
                    <Alert 
                        type="error"
                        message="Error"
                        description="An network error occurred. Please try again later."
                    />
                    }

                    { !hasError &&
                    <Loadable loading={ isSaving } fixed={ true }>
                        <Form
                            form={ formController }
                            name="notification-form"
                            layout="vertical"
                            initialValues={ notification || initialFormValues }
                            onFinish={ handleFormSubmission }
                            scrollToFirstError
                        >   
                            { /* Language Chooser */ }
                            <Form.Item 
                                label="Language"
                                name="_locale"
                                rules={ [
                                    {
                                        message: `You need to add a title and content for ${ defaultLocaleName }`,
                                        validateTrigger: [],
                                        validator: ( _, ) => {
                                            if ( currentLocaleCode === companyDefaultLocaleCode ) {
                                                return Promise.resolve();
                                            }
                                            
                                            if ( validateEnglishExists() ) {
                                                return Promise.resolve();
                                            } else {
                                                return Promise.reject();
                                            }
                                        }
                                    }
                                ] }
                            >
                                <LanguageSelectorSimple
                                    mode="select-single"
                                    languageOptions={ locales }
                                    onChange={ ( code ) => handleChangeLocale( code ) }
                                />
                            </Form.Item>
                            
                            { invalidLocaleError && 
                            <Alert
                                className="locale-error"
                                message="Required Fields"
                                description={ invalidLocaleError }
                                type="error"
                            />
                            }
                            
                            { /* Name */ }
                            <Form.Item
                                name="name"
                                label="Title"
                                rules={ [
                                    {
                                        required: true,
                                        validateTrigger: [],
                                        message: 'Notifications are required to have a title.',
                                    },
                                    {
                                        max: 50,
                                        validateTrigger: [],
                                        message: 'Notification\'s content must be less than 50 characters long.',
                                    }
                                ] }
                            >
                                <Input/>
                            </Form.Item>  
                            
                            { /* Description */ }
                            { showTinyMCE && 
                                <Form.Item
                                    name="description"
                                    label="Content" 
                                    rules={ [
                                        {
                                            message: 'Please provide this notification\'s content.',
                                            validateTrigger: [],
                                            validator: ( _, ) => {
                                                if ( TinyMCERef.current.getCharCount() > 0 ) {
                                                    return Promise.resolve();
                                                } else {
                                                    return Promise.reject();
                                                }
                                            }
                                        }
                                    ] }    
                                >
                                    <TinyMCE ref={ TinyMCERef }/>
                                </Form.Item> 
                            }

                            { !showTinyMCE &&
                                <Form.Item
                                    name="description"
                                    label="Content"                                              
                                    rules={ [
                                        {
                                            required: true,
                                            message: 'Please provide this notification\'s content.',
                                        },
                                    ] }
                                >
                                    <RichTextEditor 
                                        controls="simple"  
                                        locale={ currentLocaleCode }
                                        rawHTML={ descriptionRawHTML }
                                    />
                                </Form.Item>
                            }
                            
                            { /* isPublished Card */ }
                            <Card 
                                className="card-inner"
                                title="Publishing"
                                extra={ 
                                    <Button
                                        size="small" 
                                        shape="circle"
                                        onClick={ handlePublishedHelpClick }
                                    >
                                        <QuestionCircleOutlined />
                                    </Button>
                                }
                            >
                                <Form.Item name="isPublished">
                                    <Radio.Group>
                                        <Radio.Button value={ true }>Published</Radio.Button>
                                        <Radio.Button value={ false }>Not Published</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>

                                <Form.Item
                                    noStyle
                                    shouldUpdate={ ( prevValues, currentValues ) => 
                                        ( prevValues.isPublished !== currentValues.isPublished ) ||
                                        ( prevValues._unpublishAt !== currentValues._unpublishAt ) ||
                                        ( prevValues._publishAt !== currentValues._publishAt ) }
                                >
                                    { ( { getFieldValue } ) =>
                                        getFieldValue( 'isPublished' ) === false ? (
                                            <Form.Item  
                                                label={ `Publish On (${ timeZone })` }
                                                name="_publishAt"
                                                rules={ [
                                                    {
                                                        message: 'The publish date and time must be in the future.',
                                                        validateTrigger: [],
                                                        validator: ( _, value ) => {
                                                            if ( !value || dayjs( value ).isAfter( dayjs(), 'minute' ) ) {
                                                                return Promise.resolve();
                                                            } else {
                                                                return Promise.reject();
                                                            }
                                                        }
                                                    },
                                                    {
                                                        message: 'Please add a publish date',
                                                        validateTrigger: [],
                                                        validator: ( _, value ) => {
                                                            const pubUn = getFieldValue( '_unpublishAt' );
                                                            
                                                            // If no dates thats ok
                                                            if ( !value && !pubUn ) {
                                                                return Promise.resolve();
                                                            }

                                                            // If has unpublish date but no publish date
                                                            if ( !value  && pubUn ) {
                                                                return Promise.reject();
                                                            } else {
                                                                return Promise.resolve();
                                                            }
                                                        }
                                                    },
                                                    {
                                                        message: 'This date must be before the Publish Until date.',
                                                        validateTrigger: [],
                                                        validator: ( _, value ) => {
                                                            const pubUn = getFieldValue( '_unpublishAt' );
                                                            
                                                            if ( value && pubUn ) {
                                                                if ( dayjs( value ).isBefore( pubUn  ) ) {
                                                                    return Promise.resolve();
                                                                } else {
                                                                    return Promise.reject();
                                                                }
                                                            } else {
                                                                return Promise.resolve();
                                                            }
                                                        }
                                                    }
                                                ] }
                                            >
                                                <DatePicker 
                                                    inputReadOnly
                                                    format="YYYY-MM-DD HH:mm"
                                                    showNow={ false }
                                                    showTime={ {
                                                        minuteStep: 30,
                                                        defaultValue: dayjs( '00:00', 'HH:mm' )
                                                    } }
                                                    disabledDate={ current => {
                                                        const pubUn = getFieldValue( '_unpublishAt' );
                                                            
                                                        if ( !pubUn ) {
                                                            return !current ||
                                                                current.isBefore( dayjs(), 'day' );
                                                        } else {
                                                            return current.isBefore( dayjs(), 'day' ) ||
                                                                current.isAfter( pubUn );
                                                        }
                                                    } }
                                                />
                                            </Form.Item> 
                                        ) : null
                                    }
                                </Form.Item>
                                
                                <Form.Item
                                    noStyle
                                    shouldUpdate={ ( prevValues, currentValues ) => 
                                        ( prevValues._unpublishAt !== currentValues._unpublishAt ) ||
                                        ( prevValues._publishAt !== currentValues._publishAt ) ||
                                        ( prevValues.isPublished !== currentValues.isPublished ) }
                                >
                                    { ( { getFieldValue } ) => 
                                        <Form.Item  
                                            label={ `Publish Until (${ timeZone })` }
                                            name="_unpublishAt"
                                        >
                                            <DatePicker 
                                                inputReadOnly
                                                format="YYYY-MM-DD HH:mm"
                                                showNow={ false }
                                                showTime={ {
                                                    minuteStep: 30,
                                                    defaultValue: dayjs( '00:00', 'HH:mm' )
                                                } }
                                                disabledDate={ current => {
                                                    const pubAt = getFieldValue( '_publishAt' );
                                                    const isPub = getFieldValue( 'isPublished' );
                                                        
                                                    if ( isPub ) {
                                                        return !current ||
                                                            current.isSameOrBefore( dayjs() );
                                                    } else {
                                                        if ( pubAt ) {
                                                            return !current ||
                                                                current.isSameOrBefore( dayjs( pubAt ), 'day' );
                                                        } else {
                                                            return !current ||
                                                                current.isSameOrBefore( dayjs(), 'day' );
                                                        }
                                                    }
                                                } }
                                            />
                                        </Form.Item> 
                                    }   
                                </Form.Item>
                                
                                { showPublishedHelp &&
                                <Alert
                                    message="Publishing Notifications"
                                    description="Publishing a notification makes it visible to users. There are two optional dates that can be 
                                        set, one for the date it will be published and on for the date it will become unpublished. These are both optional
                                        and if you don't set them your notification will be visible to users immediately upon saving it."
                                    type="warning"
                                />
                                }
                            </Card>
                            
                            { /* isFeatured Card */ }
                            <Card 
                                className="card-inner"
                                title="Featured"
                                extra={
                                    <Button
                                        size="small" 
                                        shape="circle"
                                        onClick={ handleFeaturedHelpClick }
                                    >
                                        <QuestionCircleOutlined />
                                    </Button>
                                }
                            >
                                <Form.Item name="isFeatured">
                                    <Radio.Group>
                                        <Radio.Button value={ true }>Featured</Radio.Button>
                                        <Radio.Button value={ false }>Not Featured</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                                
                                <Form.Item
                                    noStyle
                                    shouldUpdate={ ( prevValues, currentValues ) => prevValues.isFeatured !== 
                                        currentValues.isFeatured }
                                >
                                    { ( { getFieldValue } ) =>
                                        getFieldValue( 'isFeatured' ) === true ? (
                                            <Form.Item
                                                label={ `Featured Until (${ timeZone })` }
                                                name="_unfeatureAt"
                                            >
                                                <DatePicker 
                                                    inputReadOnly
                                                    format="YYYY-MM-DD HH:mm"
                                                    showNow={ false }
                                                    showTime={ {
                                                        minuteStep: 30,
                                                        defaultValue: dayjs( '00:00', 'HH:mm' )
                                                    } }
                                                    disabledDate={ current =>
                                                        !current || 
                                                        current.isSameOrBefore( dayjs() )
                                                    }
                                                />
                                            </Form.Item> 
                                        ) : null
                                    }
                                </Form.Item>
                                
                                { showFeaturedHelp &&
                                <Alert
                                    message="Featured Notifications"
                                    description="When a notification is featured it appears in a popup window immediately after the user arrives 
                                        on your site. This helps the notification get more immediate attention from the user. You can also add 
                                        a date that this notification stops being featured. This date is optional. Also note that featured notifications
                                        will never appear unless they are first set to 'Published'."
                                    type="warning"
                                />
                                }
                            </Card>
                            
                            { /* isSticky Card */ }
                            <Card 
                                className="card-inner"
                                title="Sticky"
                                extra={
                                    <Button
                                        size="small" 
                                        shape="circle"
                                        onClick={ handleStickyHelpClick }
                                    >
                                        <QuestionCircleOutlined />
                                    </Button>
                                }
                            >
                                <Form.Item name="isSticky">
                                    <Radio.Group>
                                        <Radio.Button value={ true }>Sticky</Radio.Button>
                                        <Radio.Button value={ false }>Not Sticky</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>

                                <Form.Item
                                    noStyle
                                    shouldUpdate={ ( prevValues, currentValues ) => prevValues.isSticky !== 
                                        currentValues.isSticky }
                                >
                                    { ( { getFieldValue } ) =>
                                        getFieldValue( 'isSticky' ) === true ? (
                                            <Form.Item 
                                                name="weight"
                                                label="Weight"
                                            >
                                                <InputNumber 
                                                    min={ 1 } 
                                                    max={ 10 } 
                                                />
                                            </Form.Item> 
                                        ) : null
                                    }
                                </Form.Item>
                                
                                { showStickyHelp &&
                                <Alert
                                    message="Sticky Notifications"
                                    description="When a notification is sticky it will appear before all other 
                                        notifications in a popup. If there are multiple sticky notifications, 
                                        the notifications with the lowest weight will appear first (e.g., 
                                        notification with a weight of 1 will appear before notification with 
                                        weight of 3). In the bell menu, the sticky notification will appear in
                                        order of date/time created (most recent > least recent)."
                                    type="warning"
                                />
                                }
                            </Card>

                            { renderSegmentation( false ) }   
                        </Form>
                    </Loadable>
                    }
                </Card>
                }
            </Layout>

            { /* Right side button bar */ }
            { !isLoading && !hasError &&
            <Layout.Sider 
                className="side-action-bar" 
                width={ 100 } 
            >   
                <Affix offsetTop={ 95 }> 
                    <Space 
                        direction="vertical" 
                        size={ 16 } 
                    >     
                        <Button
                            disabled={ isLoading || isSaving }
                            htmlType="submit" 
                            type="primary"
                            onClick={ () => {
                                formController.submit();
                            } }
                        >
                            Save
                        </Button>
                        
                        <Button
                            disabled={ isLoading || isSaving } 
                            onClick={ () => window.history.back() }
                        >
                            Cancel
                        </Button>
                        
                        { isEditing &&
                        <DeleteButton 
                            confirmMessage= { 
                                <React.Fragment>
                                    <p>Are you <i>sure</i> you want to permanently delete this notification?
                                    This action cannot be undone.</p>
                                </React.Fragment>
                            }
                            danger
                            superSafe={ false }
                            onDelete={ handleDeleteClick }
                        >
                            Delete
                        </DeleteButton>
                        }
                    </Space> 
                </Affix>
            </Layout.Sider>
            }
        </Layout>
    );
};

export default CreateEditNotificationView;
